/**
 * Unified Class Type Definitions
 *
 * The unified class type. Every class has a category (Tumbling, Stunting,
 * Flyer, etc.) and an optional level (Beginner → Elite).
 *
 * Requirements Summary:
 * Class schedule slots may have multiple coaches; keep instructor_id as the
 * schema fallback while instructor_ids carries the full assignment list.
 *
 * Created: 2026-02-20
 */

import type { ClassTeamAthleteDiscountColumns } from '@/utils/classes/teamAthleteClassDiscount';
import { ClassScheduleSlot } from '@/components/form/fields/ScheduleSelector';
import type { AddedClassDate } from '@/utils/classes/oneOffScheduleSlots';
import type { SessionPaymentPlan } from '@/utils/billing/sessionPaymentPlan';

// ---------------------------------------------------------------------------
// Class Categories & Levels
// ---------------------------------------------------------------------------

export const CLASS_CATEGORIES = [
  'Tumbling',
  'Stunting',
  'Flyer',
  'Jumps & Flexibility',
  'Dance & Choreography',
  'Conditioning & Strength',
  'General Cheer',
  'Backspot & Bases',
  'Performance Skills',
  // Open Gym (Phase A): its own category so internal reports label pay-per-visit
  // floor time correctly. The class API forces this category when is_open_gym=true.
  'Open Gym',
] as const;

export type ClassCategory = (typeof CLASS_CATEGORIES)[number];

export const CLASS_LEVELS = ['Beginner', 'Intermediate', 'Advanced', 'Elite'] as const;
export type ClassLevel = (typeof CLASS_LEVELS)[number];

export const CLASS_CATEGORY_DEFAULTS: Record<ClassCategory, {
  defaultCapacity: number;
  defaultMonthlyRate: number;
  defaultDropInRate: number;
  defaultDurationMinutes: number;
  defaultMinAge: number;
  defaultMaxAge: number;
}> = {
  Tumbling: { defaultCapacity: 12, defaultMonthlyRate: 120, defaultDropInRate: 25, defaultDurationMinutes: 90, defaultMinAge: 6, defaultMaxAge: 18 },
  Stunting: { defaultCapacity: 16, defaultMonthlyRate: 100, defaultDropInRate: 25, defaultDurationMinutes: 75, defaultMinAge: 6, defaultMaxAge: 18 },
  Flyer: { defaultCapacity: 12, defaultMonthlyRate: 110, defaultDropInRate: 25, defaultDurationMinutes: 75, defaultMinAge: 6, defaultMaxAge: 18 },
  'Jumps & Flexibility': { defaultCapacity: 20, defaultMonthlyRate: 80, defaultDropInRate: 20, defaultDurationMinutes: 60, defaultMinAge: 5, defaultMaxAge: 18 },
  'Dance & Choreography': { defaultCapacity: 20, defaultMonthlyRate: 80, defaultDropInRate: 20, defaultDurationMinutes: 60, defaultMinAge: 4, defaultMaxAge: 18 },
  'Conditioning & Strength': { defaultCapacity: 20, defaultMonthlyRate: 70, defaultDropInRate: 15, defaultDurationMinutes: 60, defaultMinAge: 6, defaultMaxAge: 18 },
  'General Cheer': { defaultCapacity: 15, defaultMonthlyRate: 80, defaultDropInRate: 20, defaultDurationMinutes: 60, defaultMinAge: 3, defaultMaxAge: 18 },
  'Backspot & Bases': { defaultCapacity: 16, defaultMonthlyRate: 100, defaultDropInRate: 25, defaultDurationMinutes: 75, defaultMinAge: 7, defaultMaxAge: 18 },
  'Performance Skills': { defaultCapacity: 18, defaultMonthlyRate: 85, defaultDropInRate: 20, defaultDurationMinutes: 60, defaultMinAge: 4, defaultMaxAge: 18 },
  // Open Gym (Phase A): pay-per-visit floor time has no monthly tuition; keep the
  // exhaustive Record valid with sensible drop-in-only defaults. monthlyRate stays
  // 0 because the server forces monthly settings off for Open Gym.
  'Open Gym': { defaultCapacity: 30, defaultMonthlyRate: 0, defaultDropInRate: 15, defaultDurationMinutes: 120, defaultMinAge: 4, defaultMaxAge: 18 },
};

// ---------------------------------------------------------------------------
// Section Form Data
// ---------------------------------------------------------------------------
// waitlist_enabled is per-section: each schedule (class_section) has its own
// toggle. When a schedule is full and waitlist is on, parents can join that
// schedule's waitlist. Legacy class-level ClassFormData.waitlist_enabled is
// kept for back-compat with readers that haven't been migrated yet; the admin
// form writes it as "true if any section has waitlist enabled".

export interface ClassSectionFormData {
  id?: string;
  section_name: string;
  capacity?: number;
  instructor_id?: string;
  instructor_ids?: string[];
  location_id?: string;
  schedule: ClassScheduleSlot[];
  /**
   * One-off added class dates (#3354): extra single dates this schedule meets
   * (e.g. the Saturday make-up for a closed Sunday). Form-side model only —
   * on submit each one becomes a schedule slot with
   * `effective_date = end_date = date`, which the class API persists verbatim.
   */
  added_dates?: AddedClassDate[];
  monthly_rate?: number;
  drop_in_rate?: number;
  session_start_date?: string;
  session_end_date?: string;
  waitlist_enabled?: boolean;
}

// ---------------------------------------------------------------------------
// Database Record
// ---------------------------------------------------------------------------

/**
 * #5131: the class's "Team athlete discount" columns, with the two enum columns
 * narrowed here because the generated types call them plain strings.
 */
export interface UnifiedClass extends Partial<ClassTeamAthleteDiscountColumns> {
  id: string;

  class_category: ClassCategory;
  class_level?: ClassLevel | null;

  required_skills: string[];
  skills_taught: string[];

  class_schedule?: ClassScheduleSlot[];

  is_session_based: boolean;
  monthly_membership_enabled: boolean;
  drop_in_enabled: boolean;
  drop_in_availability_rule: 'always' | 'only_if_not_full' | 'extra_slots';
  extra_drop_ins_allowed: number;
  makeup_tokens_enabled: boolean;
  makeup_token_expiry_days: number;
  makeup_token_monthly_limit?: number | null;
  session_start_date?: string | null;
  session_end_date?: string | null;
  session_length_weeks?: number | null;
  /** Ongoing classes only (#4724): optional date the class first meets. */
  first_class_date?: string | null;

  // Open Gym (Phase A): discriminator flag keeping pay-per-visit floor time on
  // the class engine, plus the members-only access lever. Default false so
  // normal classes are unaffected. Behavior (forced defaults, members-only
  // enforcement) lands in later phases.
  is_open_gym: boolean;
  open_gym_members_only: boolean;

  monthly_rate: number;
  drop_in_rate: number;
  member_drop_in_enabled: boolean;
  member_drop_in_rate: number;
  family_discount_rate?: number | null;
  // "Require automatic payments" for this class. Column name is historical: since
  // plan 2026-09-05-001 PR 2 it covers BOTH billing models — each monthly invoice of
  // an ongoing class, and installments 2..N of a fixed-term program's plan. The June
  // guard that forced it off for fixed-term classes was 1d1c6dc2c; reversed on purpose.
  // Mirrors private_lessons.standing_lesson_requires_saved_payment_method.
  // Non-retroactive (enforced at enrollment checkout only). Default false.
  require_monthly_autopay?: boolean;
  // iClassPro-style "Force Calendar Month" first-month proration. Only applies
  // to monthly enrollments; subsequent months bill the full monthly_rate.
  allow_proration?: boolean;
  session_total_price?: number | null;
  session_payment_plan?: SessionPaymentPlan | null;

  min_age: number;
  max_age: number;
  class_capacity: number;
  waitlist_enabled: boolean;

  // Pricing/fee setting. When false, enrollments in this class skip the annual
  // gym membership fee and do not create an athlete_location_memberships row.
  annual_membership_required?: boolean;

  // Parent-facing display setting. The schema still uses instructor_* fields
  // for schedule assignments, but staff-facing UI calls these people coaches.
  show_coach_gender?: boolean;

  requires_evaluation?: boolean;
  parent_participation?: boolean;

  cover_image_url?: string | null;
  unavailable_dates?: string[] | null;

  name?: string;
  description?: string;
  status?: 'active' | 'inactive' | 'archived' | 'planning';
  registration_status?: 'open' | 'closed' | 'waitlist' | 'invite_only';
  organization_id?: string;
  created_at?: string;
  updated_at?: string;

  enrollment_count?: number;

  /** #4423: staff tucked this class out of the everyday Programs list. Display
   *  only — enrollment, billing, attendance, and the public catalog ignore it,
   *  and the class keeps running. Reveal with "Show hidden" on that page. */
  hidden_from_staff_list?: boolean;

  sections?: ClassSectionFormData[];

  /** Per-program policies feature: optional pre-loaded list of policy_template IDs
   *  attached to this class. The edit form's RequiredPoliciesPicker will also fetch
   *  this from the server, so this field is just a hint for forms that already have
   *  it in scope. */
  requiredPolicyIds?: string[];
}

// ---------------------------------------------------------------------------
// Form Data
// ---------------------------------------------------------------------------

/**
 * #5131: the team-discount columns ride along only when the form sends them
 * (flag on, owner or admin, not Open Gym). See teamAthleteClassDiscount.ts.
 */
export interface ClassFormData extends Partial<ClassTeamAthleteDiscountColumns> {
  name: string;
  description: string;
  cover_image_url?: string;

  class_category: ClassCategory;
  class_level?: ClassLevel | '';

  required_skills: string[];
  skills_taught: string[];

  is_session_based: boolean;
  monthly_membership_enabled: boolean;
  drop_in_enabled: boolean;
  drop_in_availability_rule: 'always' | 'only_if_not_full' | 'extra_slots';
  extra_drop_ins_allowed: number;
  makeup_tokens_enabled: boolean;
  makeup_token_expiry_days: number;
  makeup_token_monthly_limit?: number;
  session_start_date?: string;
  session_end_date?: string;
  session_length_weeks?: number;
  /** Ongoing classes only (#4724): 'YYYY-MM-DD' or '' for none. */
  first_class_date?: string;

  // Open Gym (Phase A): the create/edit form carries these so the class API can
  // persist them. Default false. When is_open_gym is true the server forces the
  // Open Gym configuration regardless of the other form values (see class API).
  is_open_gym: boolean;
  open_gym_members_only: boolean;

  monthly_rate: number;
  drop_in_rate: number;
  member_drop_in_enabled: boolean;
  member_drop_in_rate: number;
  family_discount_rate: number;
  // "Require automatic payments" (per-class, both billing models). Default false.
  // The class API coerces this server-side: only persisted true when the class is
  // eligible AND the gym can actually collect (stripe_charges_enabled) — see
  // canRequireClassAutoPay in utils/types/classAutoPay.ts.
  require_monthly_autopay: boolean;
  allow_proration?: boolean;
  session_total_price?: number;
  session_payment_plan?: SessionPaymentPlan;

  min_age: number;
  max_age: number;
  class_capacity: number;
  waitlist_enabled: boolean;

  // Pricing/fee setting. When false, enrollments in this class skip the annual
  // gym membership fee and do not create an athlete_location_memberships row.
  // Defaults true so existing and new classes preserve historical behavior.
  annual_membership_required: boolean;

  // Parent-facing display setting for whether coach gender appears beside
  // coach names when the data is available.
  show_coach_gender: boolean;

  sections: ClassSectionFormData[];

  unavailable_dates?: string[];

  status: 'active' | 'inactive' | 'archived' | 'planning';

  /** Per-program policies feature: policy_template IDs the parent must sign when
   *  enrolling in this class (in addition to org-wide is_required_for_registration
   *  policies). Empty array (or omitted) means no class-specific policies attached. */
  requiredPolicyIds?: string[];
}

// Session payment plan options (shared across all fixed-term class types).
// Defined once in @/utils/billing/sessionPaymentPlan (pure module) and re-exported here
// for the form/detail views that already import from classTypes.
export { SESSION_PAYMENT_PLAN_OPTIONS } from '@/utils/billing/sessionPaymentPlan';
export type { SessionPaymentPlan } from '@/utils/billing/sessionPaymentPlan';
